import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import * as Icons from 'react-icons/fa';

const FaSpinner = Icons.FaSpinner as any;
const FaCheckCircle = Icons.FaCheckCircle as any;

const slideIn = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ isVisible: boolean; isExiting: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  gap: 12px;
  z-index: 10001;
  min-width: 250px;
  animation: ${props => props.isExiting ? slideOut : slideIn} 0.3s ease;
  
  @media (max-width: 480px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const IconWrapper = styled.div<{ isSpinning?: boolean }>`
  color: #4CAF50;
  font-size: 18px;
  display: flex;
  align-items: center;
  animation: ${props => props.isSpinning ? spin : 'none'} 1s linear infinite;
`;

const ToastMessage = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

interface ToastProps {
  isVisible: boolean;
  message: string;
  type?: 'loading' | 'success';
  onClose?: () => void;
}

const Toast: React.FC<ToastProps> = ({ isVisible, message, type = 'loading', onClose }) => {
  const [isExiting, setIsExiting] = React.useState(false);

  useEffect(() => {
    if (isVisible && type === 'success') {
      // Auto-close success toast after 2 seconds
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onClose?.();
          setIsExiting(false);
        }, 300);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, type, onClose]);

  if (!isVisible) return null;

  return (
    <ToastContainer isVisible={isVisible} isExiting={isExiting}>
      <IconWrapper isSpinning={type === 'loading'}>
        {type === 'loading' ? <FaSpinner /> : <FaCheckCircle />}
      </IconWrapper>
      <ToastMessage>{message}</ToastMessage>
    </ToastContainer>
  );
};

export default Toast;
